// SPDX-License-Identifier: AGPL-3.0-or-later
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { Dataset, Route, ClassId, StateEntry } from '../types';
import { esc, num, pctv, SHARE_RAMP, NO_DATA, rampFor, STATE_NAME } from '../format';
import { openState } from '../main';

interface Measure { cid: ClassId; label: string; kind: 'pct' | 'count'; ref: number; thresholds: number[]; fmt: (v: number | null) => string }

export function renderMap(root: HTMLElement, d: Dataset, route: Route): void {
  // classes that carry state data (business has none)
  const stateClasses = d.meta.classOrder.filter((cid) => d.classes[cid].states.some((s) => s.value != null));

  const measures: Measure[] = stateClasses.map((cid) => {
    const states = d.classes[cid].states;
    const vals = states.map((s) => s.value).filter((v): v is number => v != null);
    const max = vals.length ? Math.max(...vals) : 1;
    if (cid === 'agricultural' || cid === 'water') {
      const ref = cid === 'agricultural' ? (d.meta.counts.agForeignSharePct ?? 10) : (d.meta.counts.waterForeignSharePct ?? 10);
      return { cid, label: d.classes[cid] && d.meta.classMeta[cid].short, kind: 'pct', ref, thresholds: [ref * 0.5, ref * 0.85, ref * 1.2, ref * 1.8], fmt: (v) => pctv(v) };
    }
    return { cid, label: d.meta.classMeta[cid].short, kind: 'count', ref: max, thresholds: [max * 0.05, max * 0.15, max * 0.35, max * 0.6], fmt: (v) => (v == null ? 'not published' : `${num(v)} ${cid === 'commercial' ? 'sites' : cid === 'mining' ? 'tenements' : 'buyers'}`) };
  });
  let measure = measures.find((m) => m.cid === route.cls) ?? measures[0];

  root.innerHTML = `
    <div class="view-head">
      <h2>The map — where foreign ownership sits</h2>
      <p>Each state and territory shaded by the chosen class, on real ABS boundaries. Farmland and water show the share of the state total that is foreign-held; other classes show the count of registered foreign interests. Colour bands are set relative to the national figure (percentages) or the top state (counts). Click a state for its full breakdown.</p>
    </div>
    <div class="controls">
      <span class="control-label">Show</span>
      <div class="seg" role="tablist" aria-label="Map class">
        ${measures.map((m) => `<button data-measure="${m.cid}" aria-pressed="${m.cid === measure.cid}">${esc(m.label)}</button>`).join('')}
      </div>
    </div>
    <div class="panel">
      <div class="map-shell"><div class="map-canvas" id="map"></div></div>
      <div class="map-legend" id="map-legend"></div>
      <p class="mini-note" id="map-note"></p>
    </div>
  `;

  const canvas = root.querySelector('#map') as HTMLElement;
  const map = L.map(canvas, { minZoom: 3, maxZoom: 8, zoomControl: true, scrollWheelZoom: false });
  map.attributionControl.setPrefix(false);
  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
    attribution: 'Tiles © CARTO · Data: ATO Register of Foreign Ownership · Boundaries: ABS ASGS (CC BY 4.0)',
    subdomains: 'abcd', minZoom: 3, maxZoom: 8,
  }).addTo(map);
  map.setView([-28.2, 134], 4);

  const stateMap = () => new Map(d.classes[measure.cid].states.map((s) => [s.code, s]));
  let byCode = stateMap();
  let layer: L.GeoJSON | null = null;

  const colourFor = (code: string) => rampFor(byCode.get(code)?.value ?? null, measure.thresholds, SHARE_RAMP);

  function bindTip(lyr: any, s: StateEntry | undefined, name: string): void {
    lyr.bindTooltip(
      `<strong>${esc(name)}</strong><br>${esc(measure.label)}: ${measure.fmt(s?.value ?? null)}${s?.combined ? '<br><em>NSW &amp; ACT combined</em>' : ''}`,
      { sticky: true, className: 'map-tip' },
    );
  }
  const styleFn = (f: any) => ({ fillColor: colourFor(String(f.properties.code)), fillOpacity: 0.85, color: '#ffffff', weight: 1 });

  async function ensureLayer(): Promise<void> {
    const geo = await fetch('data/states.geojson').then((r) => { if (!r.ok) throw new Error('boundaries failed'); return r.json(); });
    layer = L.geoJSON(geo, {
      style: styleFn,
      onEachFeature: (f: any, lyr: any) => {
        const code = String(f.properties.code);
        bindTip(lyr, byCode.get(code), f.properties.name);
        lyr.on({
          mouseover: () => lyr.setStyle({ weight: 2.4, color: '#14231d' }),
          mouseout: () => layer!.resetStyle(lyr),
          click: () => openState(code),
        });
      },
    }).addTo(map);
    try { map.fitBounds(layer.getBounds(), { padding: [10, 10] }); } catch { /* keep default */ }
  }

  function restyle(): void {
    byCode = stateMap();
    if (!layer) return;
    layer.setStyle(styleFn);
    layer.eachLayer((lyr: any) => bindTip(lyr, byCode.get(String(lyr.feature.properties.code)), lyr.feature.properties.name));
  }

  function legend(): void {
    const el = root.querySelector('#map-legend') as HTMLElement;
    const th = measure.thresholds;
    el.innerHTML =
      `<span class="legend-item">lower</span><span class="ramp">${SHARE_RAMP.map((cl) => `<span style="background:${cl}"></span>`).join('')}</span><span class="legend-item">higher</span>` +
      `<span class="legend-item"><span class="legend-swatch" style="background:${NO_DATA}"></span>not published</span>` +
      `<span class="legend-item" style="color:var(--text-tertiary)">bands at ${th.map((t) => measure.fmt(t)).join(', ')}${measure.kind === 'pct' ? ` · national ${measure.fmt(measure.ref)}` : ''}</span>`;
    (root.querySelector('#map-note') as HTMLElement).textContent =
      measure.cid === 'mining' ? 'Mining is heavily suppressed at state level — most states hold ≤5 tenement interests, shown as not published.'
      : measure.cid === 'agricultural' || measure.cid === 'water' ? 'Farmland and water combine NSW and the ACT; the ACT is shaded with the combined value.'
      : '';
  }

  root.querySelectorAll<HTMLButtonElement>('[data-measure]').forEach((b) =>
    b.addEventListener('click', () => {
      measure = measures.find((m) => m.cid === b.dataset.measure)!;
      root.querySelectorAll<HTMLButtonElement>('[data-measure]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      restyle(); legend();
    }),
  );

  void ensureLayer().then(legend);
  const ro = new ResizeObserver(() => { if (canvas.clientHeight > 50) { map.invalidateSize(); ro.disconnect(); } });
  ro.observe(canvas);
  setTimeout(() => map.invalidateSize(), 400);
  void STATE_NAME;
}
